import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {

  /**
   * A newer deploy is live. Consumed two ways: the app root hard-reloads on
   * the NEXT router navigation, and — because a user parked on one page never
   * navigates — also renders a fixed "refresh" prompt so a stale tab always
   * has a visible way forward.
   */
  readonly updateAvailable = signal(false);

  private ngZone = inject(NgZone);
  private currentHash: string | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly POLL_INTERVAL = 60_000;

  constructor() {
    this.init();
  }

  private async init(): Promise<void> {
    this.currentHash = await this.fetchVersionHash();
    if (!this.currentHash) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.checkForUpdate(), this.POLL_INTERVAL);
      // Also check the moment a backgrounded tab is refocused, so a long-idle
      // tab notices a deploy before the user clicks into a (now-missing) chunk.
      document.addEventListener('visibilitychange', this.onVisibility);
    });
  }

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      void this.checkForUpdate();
    }
  };

  private async checkForUpdate(): Promise<void> {
    const hash = await this.fetchVersionHash();
    if (hash && this.currentHash && hash !== this.currentHash) {
      // The poll runs outside Angular — re-enter so the prompt renders now,
      // not on the next unrelated change-detection pass.
      this.ngZone.run(() => this.updateAvailable.set(true));
    }
  }

  private async fetchVersionHash(): Promise<string | null> {
    try {
      const resp = await fetch(`/version.json?t=${Date.now()}`);
      if (!resp.ok) {
        return null;
      }
      const data = await resp.json();
      return data.hash || null;
    } catch {
      return null;
    }
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
}
